import { Immutable } from "immer";
import React, { type JSX } from "react";
import { FilterDistanceSelect } from "tsi-common-react/src/apps/retail/FilterDistanceSelect/index";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/MarkerMap/index";
import { formatDistanceWithSuffix } from "tsi-common-react/src/apps/retail/helpers";
import { Link } from "tsi-common-react/src/components/Link/index";
import { LocationInput } from "tsi-common-react/src/components/LocationInput/index";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import {
    SyncStoreID,
    isoPostcode,
    isoStateA2,
} from "tsi-common-react/src/models/nominals";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconRlPin from "../../../assets/img/icons/icon-rl-pin.png";

import styles from "./index.module.scss";

const PAGE_SIZE = 15;

interface ILocationProps {
    storeNumber: number;
    store: Immutable<RetailStore>;
    isFocused: boolean;
    onSelect: (storeID: SyncStoreID) => void;
}

interface IContainerProps {
    stores: Immutable<RetailStore[]>;
    focusedStoreID: SyncStoreID | null;
    location: ILocation | null;
    onBeforeInfoWindowOpen: (
        info: google.maps.InfoWindow,
        store: Immutable<RetailStore>,
    ) => void;
    onInfoWindowClose: () => void;
}

const RetailLocation = (props: ILocationProps) => {
    const staticURL = useStaticURL();
    // encode destination address for Directions link
    const destinationAddress = encodeURI(
        props.store.address +
            " " +
            props.store.address2 +
            " " +
            props.store.city +
            "," +
            isoStateA2.unwrap(props.store.state) +
            " " +
            isoPostcode.unwrap(props.store.postal),
    );
    const directionsURL = `https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`;
    let websiteLinkElement: JSX.Element | null = null;

    if (props.store.web_sealy) {
        websiteLinkElement = (
            <div className={styles.locWebsite}>
                <Link
                    href={props.store.web_sealy}
                    target="_blank"
                    className={styles.locWebsite}
                    rel="nofollow noreferrer"
                >
                    {/* TODO(i18n): Wrap user-facing string in t tagged template: {t`Website`} */}
                    Website
                    {/* TODO(i18n): Translate alt attribute with t tagged template: alt={t`Website`} */}
                    <img
                        src={`${staticURL}img/retail-locator/open-link-icon-blue.png`}
                        alt="Website"
                        loading="lazy"
                        decoding="async"
                    />
                </Link>
            </div>
        );
    }

    return (
        <li>
            <button
                className={props.isFocused ? styles.listFocus : undefined}
                onClick={() => {
                    props.onSelect(props.store.external_id);
                }}
                aria-current={props.isFocused}
            >
                <div className={styles.listLabel}>{props.storeNumber}</div>
                <div className={styles.listDetails}>
                    <div className={styles.listContent}>
                        <h4 className={styles.locName}>{props.store.name}</h4>
                        <div>{props.store.address}</div>
                        <div>{props.store.address2}</div>
                        <div>
                            <span>{props.store.city}</span>
                            ,&nbsp;
                            <span>{isoStateA2.unwrap(props.store.state)}</span>
                            &nbsp;
                            <span>
                                {isoPostcode.unwrap(props.store.postal)}
                            </span>
                        </div>
                        {props.store.phone && (
                            <div>
                                <a href={`tel:+${props.store.phone}`}>
                                    {formatPhoneNumber(props.store.phone)}
                                </a>
                            </div>
                        )}
                        <div className={styles.locDist}>
                            {props.store.distance &&
                                formatDistanceWithSuffix(
                                    props.store.distance,
                                    2,
                                )}
                        </div>
                        <div className={styles.locDirections}>
                            <a
                                href={directionsURL}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t`Directions`}
                            </a>
                        </div>
                        {websiteLinkElement}
                    </div>
                </div>
            </button>
        </li>
    );
};

const getStoreNumber = (
    stores: Immutable<RetailStore[]>,
    store: Immutable<RetailStore>,
): number => {
    return stores.indexOf(store) + 1;
};

export const RetailLocatorBlock = (props: IContainerProps): React.ReactNode => {
    const mapElem = React.useRef<MarkerMapHandle>(null);
    const listRef = React.useRef<HTMLUListElement>(null);
    const focusIndexRef = React.useRef<number | null>(null);
    const storesRef = React.useRef(props.stores);
    storesRef.current = props.stores;
    const isAllSmall = useCurrentBreakpoint().belowMobile;
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

    React.useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [props.stores]);

    React.useEffect(() => {
        const idx = focusIndexRef.current;
        if (idx === null) {
            return;
        }
        focusIndexRef.current = null;
        listRef.current
            ?.querySelectorAll<HTMLButtonElement>(":scope > li > button")
            [idx]?.focus();
    }, [visibleCount]);

    const locationInputID = "retail-locator-location-input";
    const distanceInputID = "retail-locator-distance-input";

    const visibleStores = isAllSmall
        ? props.stores.slice(0, visibleCount)
        : props.stores;
    const showLoadMore = isAllSmall && visibleCount < props.stores.length;

    return (
        <>
            <div className={styles.retailersHero}>
                <div className="container">
                    <div className={styles.retailersForm}>
                        <h3>{t`Find a Retailer`}</h3>
                        <div className={styles.retailersFormContainer}>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                <div>
                                    <label
                                        className={concatClassNames([
                                            styles.retailersFormLabel,
                                            styles.retailersFormLabelAddress,
                                            "legal",
                                        ])}
                                        htmlFor={locationInputID}
                                    >
                                        <LocationInput
                                            inputFormat="short"
                                            inputProps={{
                                                id: locationInputID,
                                                name: "bh-sl-address",
                                                placeholder: t`Zip or City, State`,
                                            }}
                                        />
                                    </label>
                                    <label
                                        className={concatClassNames([
                                            styles.retailersFormLabel,
                                            styles.retailersFormLabelDistance,
                                            "legal",
                                        ])}
                                        htmlFor={distanceInputID}
                                    >
                                        <FilterDistanceSelect
                                            selectProps={{
                                                id: distanceInputID,
                                                className:
                                                    styles.retailersFormLabelArrow,
                                                name: "bh-sl-maxdistance",
                                                title: t`Retailer Distance`,
                                            }}
                                        />
                                    </label>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="bh-sl-map-container"
                className={concatClassNames([
                    styles.bhSlMapContainer,
                    "container",
                ])}
            >
                <MarkerMap
                    ref={mapElem}
                    stores={props.stores}
                    markerOptions={(store: Immutable<RetailStore>) => {
                        return {
                            icon: iconRlPin,
                            label: {
                                text: `${getStoreNumber(props.stores, store)}`,
                                color: "#000",
                                className: styles.markerLabel,
                                fontWeight: "light",
                            },
                        };
                    }}
                    elemProps={{
                        id: "bh-sl-map",
                        className: styles.bhSlMap,
                    }}
                    onBeforeInfoWindowOpen={(info, store) => {
                        const idx = storesRef.current.findIndex(
                            (s) => s.external_id === store.external_id,
                        );
                        if (idx >= 0) {
                            setVisibleCount((c) =>
                                Math.max(
                                    c,
                                    Math.ceil((idx + 1) / PAGE_SIZE) *
                                        PAGE_SIZE,
                                ),
                            );
                        }
                        props.onBeforeInfoWindowOpen(info, store);
                    }}
                    onInfoWindowClose={props.onInfoWindowClose}
                />
                <div className={styles.bhSlLocList}>
                    <ul ref={listRef}>
                        {visibleStores.map((store, i) => (
                            <RetailLocation
                                {...props}
                                key={`${store.external_id}`}
                                storeNumber={i + 1}
                                store={store}
                                isFocused={
                                    props.focusedStoreID === store.external_id
                                }
                                onSelect={(storeID) => {
                                    mapElem.current?.onActivateMarker(storeID);
                                }}
                            />
                        ))}
                        {props.stores.length <= 0 && (
                            <li>
                                <div className={styles.bhSlNoresultsTitle}>
                                    {t`No results`}
                                </div>
                                <br />
                                <div className={styles.bhSlNoresultsDesc}>
                                    {t`No locations were found with the given criteria. Please modify your selections or input.`}
                                </div>
                            </li>
                        )}
                    </ul>
                    {showLoadMore && (
                        <button
                            type="button"
                            className={concatClassNames([
                                styles.retailersLoadMore,
                                "button",
                            ])}
                            onClick={() => {
                                focusIndexRef.current = visibleCount;
                                setVisibleCount((count) => count + PAGE_SIZE);
                            }}
                        >
                            {t`Load more retailers`}
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};
